import { t, tf } from './lib/i18n';
// Audit anchor: GROUP · 3–8 PEOPLE
import { useEffect, useState } from 'react';
import {
  answerGroupRound, cancelGroupRoom, createGroupRoom, joinGroupRoom, leaveGroupRoom, loadGroupRoom, nextGroupRound,
  reactToGroupRound, refreshGroupShare, rememberGroupToken, rememberedGroupToken, setGroupReady, startGroupRoom, watchGroupRoom,
  type GroupChoice, type GroupInteraction, type GroupReaction, type GroupRoomSnapshot, type GroupShare
} from './lib/group-core';
import { SafetyControls } from './SafetyControls';
import { RecoveryNotice } from './RecoveryNotice';
import { useFreshMoment } from './lib/experience';
import { haptics } from './lib/haptics';
import { trackAnalytics } from './lib/analytics';
import { VisualProviderCredit } from './VisualProviderCredit';


function GroupChoiceMedia({imageUrl,fallbackImageUrl,alt}:{imageUrl:string|null;fallbackImageUrl:string|null;alt:string}){
  const [source,setSource]=useState(imageUrl??fallbackImageUrl);
  const [fallback,setFallback]=useState(Boolean(!imageUrl&&fallbackImageUrl));
  useEffect(()=>{setSource(imageUrl??fallbackImageUrl);setFallback(Boolean(!imageUrl&&fallbackImageUrl));},[imageUrl,fallbackImageUrl]);
  if(!source)return null;
  const lineArt=fallback||source.includes('/visuals/line/');
  return <span className={`answer-choice-media${lineArt?' line-art':''}`}><img className={`answer-choice-image${lineArt?' line-art':''}`} src={source} alt={alt} loading="eager" onError={()=>{if(!fallback&&fallbackImageUrl){setSource(fallbackImageUrl);setFallback(true);}else setSource(null);}}/></span>;
}

const interactionName = (interaction: GroupInteraction): string => interaction === 'same_brain' ? t('SAME BRAIN') : interaction === 'read_me' ? t('READ ME') : t("DON'T MATCH");
const openShare = (share: GroupShare, interactionType: GroupInteraction): void => {
  const webApp = window.Telegram?.WebApp;
  trackAnalytics('share_opened', { shareKind: 'group', interactionType });
  if (share.preparedMessageId && webApp?.shareMessage) webApp.shareMessage(share.preparedMessageId, (sent) => {
    if (sent) trackAnalytics('share_sent', { shareKind: 'group', interactionType });
  });
  else if (webApp?.openTelegramLink) webApp.openTelegramLink(share.fallbackShareUrl);
  else window.open(share.fallbackShareUrl, '_blank', 'noopener,noreferrer');
};

const groupResult = (snapshot: GroupRoomSnapshot): { label: string; value: number; detail: string; title: string } => {
  const game = snapshot.game;
  if (!game) return { label: t('GROUP COMPLETE'), value: 0, detail: '', title: t('EVERYONE WAS IN.') };
  if (snapshot.room.interactionType === 'same_brain') return {
    label: t('GROUP SYNC'), value: game.score.syncPercent,
    detail: tf('{count} collective reveals', { count: game.score.completedRounds }), title: game.score.syncPercent >= 80 ? t('ONE SHARED BRAIN.') : t('MANY BRAINS. ONE ROOM.')
  };
  if (snapshot.room.interactionType === 'read_me') return {
    label: t('GROUP READ'), value: game.score.accuracyPercent,
    detail: tf('{correct} of {total} predictions landed', { correct: game.score.correctPredictions, total: game.score.totalPredictions }),
    title: game.score.accuracyPercent >= 80 ? t('YOU KNOW EACH OTHER.') : t('STILL FULL OF SURPRISES.')
  };
  return {
    label: t('GROUP SURVIVAL'), value: game.score.survivalPercent,
    detail: tf('{survived} of {total} rounds survived', { survived: game.score.survivedRounds, total: game.score.completedRounds }),
    title: game.score.survivalPercent >= 80 ? t('EXPERT AVOIDANCE.') : t('CHAOTIC COLLISIONS. 😂')
  };
};

function GroupGame({ snapshot, token, initData, userId, connected, busy, error, perform, onReplay, onHome }: {
  snapshot: GroupRoomSnapshot; token: string; initData: string; userId: string; connected: boolean; busy: boolean;
  error: string | null; perform: (operation: () => Promise<GroupRoomSnapshot>) => Promise<boolean>;
  onReplay: () => void; onHome: () => void;
}) {
  const [pendingChoice, setPendingChoice] = useState<GroupChoice | null>(null);
  const game = snapshot.game;
  const round = game?.currentRound;
  const interaction = snapshot.room.interactionType;
  const safety = <SafetyControls initData={initData} context={{ groupRoomId: snapshot.room.id }} targets={snapshot.participants.filter((participant) => participant.userId !== userId).map((participant) => ({ userId: participant.userId, firstName: participant.firstName }))} />;
  const revealKey = round?.phase === 'revealed' ? `${snapshot.room.id}:reveal:${round.id}` : null;
  const resultKey = snapshot.room.status === 'completed' ? `${snapshot.room.id}:result` : null;
  const allLockedKey = round?.myAnswer && round.lockedCount === round.requiredCount ? `${snapshot.room.id}:locked:${round.id}` : null;
  const freshReveal = useFreshMoment(revealKey, () => round?.reveal?.dontMatch?.boom ? haptics.matchReveal() : haptics.differentReveal());
  const freshResult = useFreshMoment(resultKey, haptics.finalResult);
  useFreshMoment(allLockedKey, haptics.bothLocked);

  if (!game || !round) return <><main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Preparing the group game…')}</p></main>{safety}</>;

  if (snapshot.room.status === 'completed') {
    const result = groupResult(snapshot);
    return <>
      <main className={freshResult ? 'app-shell game-screen result-screen group-game-screen energy-peak fresh-result' : 'app-shell game-screen result-screen group-game-screen energy-peak'}>
        <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{interactionName(interaction)} · {t('GROUP')}</span></header>
        <section className="compatibility-result group-result">
          <p>{result.label}</p><div className="score-number">{result.value}%</div><h1>{result.title}</h1><p>{result.detail}</p>
          <div className="collective-dots" aria-label={tf('{count} participants', { count: snapshot.room.participantCount })}>{snapshot.participants.map((participant) => <span key={participant.userId} className="presence-dot" />)}</div>
        </section>
        <div className="result-actions">
          <button className="primary-action" onClick={onReplay}>{t('PLAY AGAIN')}</button>
          <button className="text-action" onClick={onHome}>{t('HOME')}</button>
        </div>
      </main>
      {safety}</>;
  }

  if (round.phase === 'revealed' && round.reveal) {
    const reveal = round.reveal;
    const headline = interaction === 'same_brain' ? tf('{percent}% IN SYNC', { percent: reveal.sameBrain?.syncPercent ?? 0 })
      : interaction === 'read_me' ? tf('{name} CHOSE {choice}', { name: round.spotlight?.firstName.toUpperCase() ?? t('THE SPOTLIGHT'), choice: reveal.readMe?.actual.label ?? '' })
        : reveal.dontMatch?.survived ? t('SURVIVED') : t('BOOM — EVERYONE MATCHED');
    return <>
      <main className={freshReveal ? 'app-shell game-screen reveal-screen group-game-screen energy-expressive fresh-reveal' : 'app-shell game-screen reveal-screen group-game-screen energy-expressive'}>
        <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('ROUND')} {round.number} / {game.totalRounds}</span></header>
        <RecoveryNotice interrupted={!connected} preserved={t('REVEAL PRESERVED')} />
        <section className="reveal-stage group-reveal-stage" aria-live="polite">
          <p className="both-in">{t('EVERYONE IN.')}</p><h1 className={reveal.dontMatch?.boom ? 'different-result' : 'match-result'}>{headline}</h1>
          {interaction === 'read_me' && reveal.readMe ? <div className="spotlight-reveal">
            <div className="human-answer"><span>{tf('{name} ACTUALLY', { name: reveal.readMe.actual.firstName.toUpperCase() })}</span><strong>{reveal.readMe.actual.label}</strong></div>
            <p><strong>{reveal.readMe.correctPredictors.length}</strong> {t('read it right')}</p>
            <p className="compact-names">{reveal.readMe.correctPredictors.map((human) => human.firstName).join(' · ') || t('Nobody called it this time')}</p>
          </div> : <div className="group-split">
            {reveal.sides.map((side) => <article className="group-side" key={side.key}><strong>{side.label}</strong><span>{side.count}</span><p>{side.names.join(' · ') || '—'}</p></article>)}
          </div>}
          <div className="reaction-row group-reactions" aria-label={t('React to the collective reveal')}>
            {(['😂', '😳', '🔥', '🤝'] as const).map((reaction: GroupReaction) => {
              const count = reveal.reactions.find((item) => item.reaction === reaction)?.count ?? 0;
              return <button key={reaction} type="button" disabled={busy || Boolean(reveal.myReaction)} className={reveal.myReaction === reaction ? 'reaction selected' : 'reaction'} aria-label={`${tf('React {reaction}', { reaction })}${count ? tf(', {count} reactions', { count }) : ''}`} aria-pressed={reveal.myReaction === reaction} onClick={() => { haptics.reactionSent(); void perform(() => reactToGroupRound(token, initData, reaction)); }}>{reaction}{count ? <small aria-hidden="true">{count}</small> : null}</button>;
            })}
          </div>
        </section>
        {snapshot.viewerRole === 'host' ? <button className="primary-action" disabled={busy} onClick={() => void perform(() => nextGroupRound(token, initData))}>{round.number >= game.totalRounds ? t('SEE GROUP RESULT') : t('NEXT')}</button> : <p className="waiting-copy">{t('The host moves the room forward.')}</p>}
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
      </main>
      {safety}</>;
  }

  const roleCopy = interaction === 'read_me'
    ? round.role === 'target' ? t('YOUR REAL CHOICE · EVERYONE IS READING YOU') : tf('READ {name}', { name: round.spotlight?.firstName.toUpperCase() ?? t('THE SPOTLIGHT') })
    : interaction === 'dont_match' ? t('AVOID THE WHOLE ROOM') : t('WHAT WILL THE ROOM CHOOSE?');
  const question = interaction === 'read_me' && round.role === 'predictor'
    ? round.spotlight ? tf('What would {name} choose?', { name: round.spotlight.firstName }) : t('What would they choose?') : round.question;
  return <>
    <main className={round.myAnswer && round.lockedCount === round.requiredCount ? 'app-shell game-screen answer-screen group-game-screen energy-anticipation' : 'app-shell game-screen answer-screen group-game-screen energy-focused'}>
      <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('ROUND')} {round.number} / {game.totalRounds}</span></header>
      <RecoveryNotice interrupted={!connected} preserved={t('CHOICE PRESERVED')} />
      <section className="question-stage">
        <p className="read-role">{roleCopy}</p><h1>{question}</h1>
        {interaction === 'read_me' && round.role === 'predictor' ? <p className="prompt-context">{round.question}</p> : null}
        <div className={`answer-options${round.options.some(option=>Boolean(option.imageUrl||option.fallbackImageUrl))?' visual-choice-grid':''}`}>
          {round.options.map((option) => {
            const selected = round.myAnswer?.key === option.key || pendingChoice === option.key;
            return <button key={`${round.id}-${option.key}`} className={selected ? 'answer-option selected' : 'answer-option'} aria-pressed={selected} aria-label={`${option.label}${round.myAnswer?.key === option.key ? t(', locked') : ''}`} disabled={busy || Boolean(round.myAnswer)} onClick={() => {
              haptics.answerTap();
              trackAnalytics('answer_submitted', { interactionType: interaction, choice: option.key, roundNumber: round.number, contextType: 'group' });
              setPendingChoice(option.key);
              void perform(() => answerGroupRound(token, initData, option.key)).then((accepted) => { if (accepted) haptics.answerLocked(); }).finally(() => setPendingChoice(null));
            }}><GroupChoiceMedia imageUrl={option.imageUrl} fallbackImageUrl={option.fallbackImageUrl} alt={option.imageAlt??option.label}/><span className="answer-choice-label">{option.label}</span>{round.myAnswer?.key === option.key ? <small>{t('✓ LOCKED')}</small> : null}</button>;
          })}
        </div>
        <VisualProviderCredit alts={round.options.map((option) => option.imageAlt)} />
        <div className="group-lock-state"><strong>{tf('{locked} / {total} LOCKED', { locked: round.lockedCount, total: round.requiredCount })}</strong><div className="collective-dots" aria-hidden="true">{Array.from({ length: round.requiredCount }, (_, index) => <span key={index} className={index < round.lockedCount ? 'presence-dot locked' : 'presence-dot empty'} />)}</div></div>
        {round.myAnswer ? <p className="waiting-copy">{t('Your choice is private. Waiting for the room…')}</p> : null}
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
      </section>
    </main>
    {safety}</>;
}

export function GroupExperience({ initData, userId, initialToken, onHome }: {
  initData: string; userId: string; initialToken?: string; onHome: () => void;
}) {
  const [token, setToken] = useState<string | null>(initialToken ?? rememberedGroupToken());
  const [selected, setSelected] = useState<GroupInteraction | null>(null);
  const [snapshot, setSnapshot] = useState<GroupRoomSnapshot | null>(null);
  const [share, setShare] = useState<GroupShare | null>(null);
  const [connected, setConnected] = useState(true);
  const [busy, setBusy] = useState(Boolean(token));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setBusy(true);
    void loadGroupRoom(token, initData).then((next) => {
      if (!cancelled) { setSnapshot(next); setSelected(next.room.interactionType); setBusy(false); }
    }).catch((reason: unknown) => {
      if (!cancelled) { setError(reason instanceof Error ? reason.message : t('Could not open this group.')); setBusy(false); }
    });
    return () => { cancelled = true; };
  }, [token, initData]);

  useEffect(() => {
    if (!token) return;
    return watchGroupRoom({
      token, initData, onConnection: setConnected,
      onSnapshot: (next) => setSnapshot((current) => !current || next.room.revision >= current.room.revision ? next : current)
    });
  }, [token, initData, snapshot?.viewerRole]);

  useEffect(() => {
    if (!token || snapshot?.room.status !== 'lobby') return;
    const delay = Math.max(0, Math.min(2_147_000_000, new Date(snapshot.room.expiresAt).getTime() - Date.now() + 250));
    const timer = window.setTimeout(() => void loadGroupRoom(token, initData).then(setSnapshot).catch(() => undefined), delay);
    return () => window.clearTimeout(timer);
  }, [token, initData, snapshot?.room.status, snapshot?.room.expiresAt]);

  useEffect(() => {
    if (token && snapshot && snapshot.viewerRole !== 'unjoined') rememberGroupToken(token);
  }, [token, snapshot?.viewerRole]);

  const perform = async (operation: () => Promise<GroupRoomSnapshot>): Promise<boolean> => {
    setBusy(true); setError(null);
    try { setSnapshot(await operation()); return true; }
    catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not update this group.')); return false; }
    finally { setBusy(false); }
  };

  const create = async (): Promise<void> => {
    if (!selected) return;
    setBusy(true); setError(null);
    try {
      const created = await createGroupRoom(initData, selected);
      setToken(created.token); rememberGroupToken(created.token); setSnapshot(created.snapshot); setShare(created.share);
      if (created.share) openShare(created.share, selected);
    } catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not create this group.')); }
    finally { setBusy(false); }
  };

  const shareAgain = async (): Promise<void> => {
    if (!token) return;
    setBusy(true); setError(null);
    try { const next = share ?? await refreshGroupShare(token, initData); setShare(next); openShare(next, snapshot?.room.interactionType ?? selected ?? 'same_brain'); }
    catch (reason) { setError(reason instanceof Error ? reason.message : t('Could not open sharing.')); }
    finally { setBusy(false); }
  };

  const exitHome = (): void => { rememberGroupToken(null); onHome(); };
  const replay = (): void => {
    trackAnalytics('group_replay_started', { interactionType: snapshot?.room.interactionType ?? selected ?? 'same_brain' });
    rememberGroupToken(null); setToken(null); setSnapshot(null); setShare(null); setError(null); setBusy(false);
  };

  if (!token) {
    return (
      <main className="app-shell home-screen interaction-screen energy-calm">
        <header className="home-header"><div className="brand-mark">UTAGE</div></header>
        <section className="interaction-heading"><p className="section-label">{t('GROUP')} · 3–8</p><h1>{t('OPEN A ROOM')}</h1><p className="quiet-copy">{t('Pick the shared energy, then invite your people.')}</p></section>
        <section className="context-picker">
          <div className="stranger-mode-row" role="group" aria-label={t('Choose a group interaction')}>
            {(['same_brain', 'read_me', 'dont_match'] as const).map((interaction) => <button key={interaction} className={selected === interaction ? 'mode-chip selected' : 'mode-chip'} onClick={() => { trackAnalytics('interaction_selected', { contextType: 'group', interactionType: interaction }); setSelected(interaction); }}>{interactionName(interaction)}</button>)}
          </div>
          <button className="primary-action" disabled={!selected || busy} onClick={() => void create()}>{busy ? t('OPENING…') : t('CREATE GROUP')}</button>
          <button className="text-action" disabled={busy} onClick={exitHome}>{t('HOME')}</button>
          {error ? <p className="inline-error" role="alert">{error}</p> : null}
        </section>
      </main>
    );
  }

  if (busy && !snapshot) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><p className="quiet-copy">{t('Opening group…')}</p></main>;
  if (!snapshot) return <main className="app-shell quiet-screen"><div className="brand-mark">UTAGE</div><h1 className="error-title">{t('Group unavailable.')}</h1><p className="quiet-copy">{error}</p><button className="secondary-action" onClick={exitHome}>{t('HOME')}</button></main>;

  if (snapshot.room.status === 'cancelled' || snapshot.room.status === 'expired') {
    return <main className="app-shell challenge-screen"><div className="brand-mark">UTAGE</div><section className="challenge-card"><p className="eyebrow">{t('GROUP CLOSED')}</p><h1>{snapshot.room.status === 'expired' ? t('This room expired.') : t('The host cancelled this room.')}</h1><button className="primary-action" onClick={exitHome}>{t('HOME')}</button></section></main>;
  }

  if (snapshot.viewerRole === 'unjoined') {
    const alreadyStarted = snapshot.room.status === 'active' || snapshot.room.status === 'completed';
    return (
      <main className="app-shell challenge-screen energy-focused">
        <div className="brand-mark">UTAGE</div>
        <section className="challenge-card">
          <p className="eyebrow">{tf('{name} OPENED A GROUP', { name: snapshot.room.host.firstName.toUpperCase() })}</p>
          <div className="group-dots" aria-hidden="true">● ● ●</div>
          <h1>{interactionName(snapshot.room.interactionType)}</h1>
          <p className="challenge-question">{alreadyStarted ? t('This group has already started.') : tf('{joined} / {max} humans joined', { joined: snapshot.room.participantCount, max: snapshot.room.maxParticipants })}</p>
          {!alreadyStarted ? <button className="primary-action" disabled={busy} onClick={() => void perform(() => joinGroupRoom(token, initData))}>{busy ? t('JOINING…') : t('JOIN GROUP')}</button> : null}
          <button className="text-action" disabled={busy} onClick={exitHome}>{t('HOME')}</button>
          {error ? <p className="inline-error">{error}</p> : null}
        </section>
      </main>
    );
  }

  if (snapshot.room.status === 'active' || snapshot.room.status === 'completed') return <GroupGame
    snapshot={snapshot} token={token} initData={initData} userId={userId} connected={connected} busy={busy} error={error}
    perform={perform} onReplay={replay} onHome={exitHome}
  />;

  const me = snapshot.participants.find((participant) => participant.userId === userId);
  return <>
    <main className="app-shell group-screen energy-calm">
      <header className="game-header"><div className="brand-mark">UTAGE</div><span className="round-label">{t('GROUP LOBBY')}</span></header>
      <RecoveryNotice interrupted={!connected} preserved={t('ROOM PRESERVED')} />
      <section className="group-lobby">
        <p className="eyebrow">{interactionName(snapshot.room.interactionType)}</p>
        <h1>{snapshot.room.participantCount} / {snapshot.room.maxParticipants}</h1>
        <p>{tf('{ready} ready · minimum {min}', { ready: snapshot.room.readyCount, min: snapshot.room.minParticipants })}</p>
        <div className="collective-dots" aria-hidden="true">{Array.from({ length: snapshot.room.maxParticipants }, (_, index) => <span key={index} className={index < snapshot.room.participantCount ? 'presence-dot' : 'presence-dot empty'} />)}</div>
        <div className="participant-list">
          {snapshot.participants.map((participant) => <div className="participant-row" key={participant.userId}><span>{participant.firstName}{participant.role === 'host' ? ` · ${t('HOST')}` : ''}</span><strong>{participant.presence === 'reconnecting' ? t('RECONNECTING') : participant.ready ? t('✓ READY') : t('NOT READY')}</strong></div>)}
        </div>
      </section>
      <div className="group-actions">
        <button className={me?.ready ? 'secondary-wide ready-toggle' : 'primary-action'} disabled={busy} onClick={() => void perform(() => setGroupReady(token, initData, !me?.ready))}>{me?.ready ? t('I’M NOT READY') : t('I’M READY')}</button>
        {snapshot.viewerRole === 'host' ? <>
          <button className="secondary-wide" disabled={busy} onClick={() => void shareAgain()}>{t('INVITE PEOPLE')}</button>
          <button className="primary-action" disabled={busy || !snapshot.room.canStart} onClick={() => void perform(() => startGroupRoom(token, initData))}>{snapshot.room.canStart ? t('START GROUP') : tf('WAITING FOR {count} READY', { count: Math.max(snapshot.room.minParticipants - snapshot.room.participantCount, 0) || snapshot.room.participantCount - snapshot.room.readyCount })}</button>
          <button className="text-action" disabled={busy} onClick={() => { if (window.confirm(t('Cancel this group room?'))) void perform(() => cancelGroupRoom(token, initData)); }}>{t('CANCEL ROOM')}</button>
        </> : <button className="text-action" disabled={busy} onClick={() => void perform(() => leaveGroupRoom(token, initData)).then((left) => { if (left) exitHome(); })}>{t('LEAVE GROUP')}</button>}
        {error ? <p className="inline-error" role="alert">{error}</p> : null}
      </div>
    </main>
    <SafetyControls initData={initData} context={{ groupRoomId: snapshot.room.id }} targets={snapshot.participants.filter((participant) => participant.userId !== userId).map((participant) => ({ userId: participant.userId, firstName: participant.firstName }))} />
  </>;
}
